import React, { useState, useEffect } from 'react';
import { cookieManager, COOKIE_TYPES } from '../utils/cookieManager';

const CookieSettings = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState({
    [COOKIE_TYPES.NECESSARY]: true,
    [COOKIE_TYPES.ANALYTICS]: false,
    [COOKIE_TYPES.MARKETING]: false,
    [COOKIE_TYPES.FUNCTIONAL]: false
  });
  const [consentDate, setConsentDate] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Load current preferences when modal opens
      setPreferences(cookieManager.getPreferences());
      setConsentDate(cookieManager.getConsentDate());
    }
  }, [isOpen]);

  const handlePreferenceChange = (type, value) => {
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleSavePreferences = () => {
    cookieManager.setConsent(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    const allConsent = {
      [COOKIE_TYPES.NECESSARY]: true,
      [COOKIE_TYPES.ANALYTICS]: true,
      [COOKIE_TYPES.MARKETING]: true,
      [COOKIE_TYPES.FUNCTIONAL]: true
    };
    setPreferences(allConsent);
    cookieManager.setConsent(allConsent);
    onClose();
  };

  const handleRejectAll = () => {
    const necessaryOnly = {
      [COOKIE_TYPES.NECESSARY]: true,
      [COOKIE_TYPES.ANALYTICS]: false,
      [COOKIE_TYPES.MARKETING]: false,
      [COOKIE_TYPES.FUNCTIONAL]: false
    };
    setPreferences(necessaryOnly);
    cookieManager.setConsent(necessaryOnly);
    onClose();
  };

  const handleRevokeConsent = () => {
    if (window.confirm('Are you sure you want to revoke all cookie consent? This will clear all stored cookies and reload the page.')) {
      cookieManager.revokeConsent();
      onClose();
    }
  };

  const cookieInfo = {
    [COOKIE_TYPES.NECESSARY]: {
      title: 'Necessary Cookies',
      description: 'These cookies are essential for the website to function properly. They enable basic features like page navigation and access to secure areas.',
      details: [
        'Session management and authentication',
        'Form submission and validation',
        'Security and fraud prevention',
        'Basic website functionality'
      ],
      required: true
    },
    [COOKIE_TYPES.ANALYTICS]: {
      title: 'Analytics Cookies',
      description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously via Google Analytics.',
      details: [
        'Google Analytics tracking',
        'Page view statistics',
        'User interaction metrics',
        'Performance monitoring'
      ],
      required: false
    },
    [COOKIE_TYPES.FUNCTIONAL]: {
      title: 'Functional Cookies',
      description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.',
      details: [
        'User preferences storage',
        'Language and region settings',
        'Theme and display preferences',
        'Accessibility settings'
      ],
      required: false
    },
    [COOKIE_TYPES.MARKETING]: {
      title: 'Marketing Cookies',
      description: 'These cookies are used to track visitors across websites to display relevant advertisements and marketing content.',
      details: [
        'Social media integration',
        'Advertisement targeting',
        'Cross-site tracking',
        'Marketing campaign analysis'
      ],
      required: false
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Cookie Settings</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-terrapin-blue focus:ring-offset-2 rounded"
            >
              ×
            </button>
          </div>
          {consentDate && (
            <p className="text-sm text-gray-600 mt-2">
              Last updated: {consentDate.toLocaleDateString()} at {consentDate.toLocaleTimeString()}
            </p>
          )}
        </div>

        <div className="p-4 sm:p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Manage Your Cookie Preferences</h3>
            <p className="text-gray-700 text-sm">
              We use cookies and similar technologies to provide the best experience on our website. 
              You can choose which types of cookies you want to allow. Note that blocking some types 
              of cookies may impact your experience on our site.
            </p>
          </div>

          <div className="space-y-6">
            {Object.entries(cookieInfo).map(([type, info]) => (
              <div key={type} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-900">{info.title}</h4>
                      {info.required && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{info.description}</p>
                    
                    <details className="text-sm">
                      <summary className="cursor-pointer text-terrapin-blue hover:text-terrapin-red font-medium">
                        View Details
                      </summary>
                      <ul className="mt-2 space-y-1 text-gray-600 ml-4">
                        {info.details.map((detail, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-terrapin-red mt-1">•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </div>

                  <div className="flex-shrink-0 ml-4">
                    {info.required ? (
                      <div className="w-14 h-7 bg-terrapin-red rounded-full flex items-center justify-end px-1">
                        <div className="w-5 h-5 bg-white rounded-full"></div>
                      </div>
                    ) : (
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences[type]}
                          onChange={(e) => handlePreferenceChange(type, e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-14 h-7 rounded-full flex items-center px-1 transition-colors ${
                          preferences[type] ? 'bg-terrapin-red justify-end' : 'bg-gray-300 justify-start'
                        }`}>
                          <div className="w-5 h-5 bg-white rounded-full transition-transform"></div>
                        </div>
                      </label>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <button
                onClick={handleSavePreferences}
                className="btn-base btn-primary text-sm focus:outline-none focus:ring-2 focus:ring-terrapin-red focus:ring-offset-2"
              >
                Save Preferences
              </button>
              
              <button
                onClick={handleAcceptAll}
                className="btn-base btn-secondary text-sm focus:outline-none focus:ring-2 focus:ring-terrapin-blue focus:ring-offset-2"
              >
                Accept All
              </button>
              
              <button
                onClick={handleRejectAll}
                className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-6 py-2 rounded-md font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-terrapin-red focus:ring-offset-2"
              >
                Reject All
              </button>
              
              <button
                onClick={handleRevokeConsent}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-md font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-auto"
              >
                Revoke All Consent
              </button>
            </div>
            
            <div className="mt-4 text-xs text-gray-500">
              <p>
                For more information about how we use cookies, please read our{' '}
                <a href="/privacy-policy" className="text-terrapin-blue hover:text-terrapin-red underline">
                  Privacy Policy
                </a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default CookieSettings; 